import React, { useEffect, useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingView from "../Common/LoadingView";
import { Box, Button, Text, CheckBox, Anchor, Layer } from "grommet";
import { clearPaymentType } from "../../features/paymentSlice";
import { Alert, CircleInformation } from "grommet-icons";
import { firestore } from "../../firebase";
import { doc, setDoc } from "firebase/firestore";
import { DateTime } from "luxon";
import TermsConditions from "./TermsConditions";

export default function CheckoutForm({ paymentAmount }) {
  let navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [openTC, setOpenTC] = useState(false);
  const [acceptTC, setAcceptTC] = useState();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const slotSelected = useSelector((state) => state.current.bookingInfo);
  const companyInfo = useSelector((state) => state.current.companyInfo);
  const currentPerson = useSelector((state) => state.payment.bookingDetails);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      console.log("switch case");
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe, slotSelected, currentPerson]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.REACT_APP_DOMAIN}/confirm`,
        payment_method_data: {
          billing_details: {
            name: currentPerson.name,
            email: currentPerson.email,
          },
        },
      },
      redirect: "if_required",
    });

    if (!error) {
      try {
        setDoc(doc(firestore, "bookings", slotSelected.id), {
          bookingStatus: "new-paid",
          companyId: slotSelected.companyId,
          companyName: companyInfo.companyName,
          companyLogo: companyInfo.companyLogo,
          paymentMethod: "credit-card",
          notes: currentPerson.details,
          fees: 0,
          location: slotSelected.locations,
          price: slotSelected.currentPrice,
          originalPrice: slotSelected.originalPrice,
          servUniqueId: slotSelected.servUniqueId,
          serviceName: slotSelected.serviceName,
          serviceId: slotSelected.serviceId,
          startTime: DateTime.fromSeconds(slotSelected.startTime).toJSDate(),
          endTime: DateTime.fromSeconds(slotSelected.endTime).toJSDate(),
          customerEmail: currentPerson.email,
          customerName: currentPerson.name,
          customerPhone: currentPerson.phone,
          bookingDetails: slotSelected.bookingDetails,
          companyURL: companyInfo.companyURL,
          customerTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          addOns: slotSelected?.addOnList || [],
        }).then(() => {
          navigate(`/confirm/${slotSelected.id}`);
        });
      } catch {
        console.log("there was an error");
      }
    } else {
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message);
      } else {
        setMessage("An unexpected error occured.");
      }
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />

      {message && (
        <Box direction="row" gap="small" margin={{ top: "0.5em" }}>
          <Alert color="status-critical" />
          <Text color="status-critical">{message}</Text>
        </Box>
      )}

      {isLoading || !stripe || !elements ? (
        <>
          <LoadingView />
        </>
      ) : (
        <Box gap="small" margin={{ top: "1.5em", bottom: "10px" }}>
          <Box gap="small" direction="row">
            <CheckBox
              checked={acceptTC}
              onChange={() => setAcceptTC(!acceptTC)}
            />
            <Text weight="bold">
              I have read and agree to the{" "}
              <Anchor
                onClick={() => setOpenTC(true)}
                label="terms & conditions"
              ></Anchor>{" "}
              in order to book this session.
            </Text>
          </Box>
          <Box direction="row" gap="small">
            <CircleInformation size="medium" />
            <Text>
              Credit card payments will incur an additional 2.9% + 30
              <span>&#162; </span>
              surcharge that goes directly to the payment platform.
            </Text>
          </Box>

          <Button
            primary
            type="submit"
            disabled={!acceptTC || isLoading || !stripe || !elements}
            label={`Pay $${(paymentAmount / 100).toFixed(2)}`}
            fill
          />
          <Button
            secondary
            label="Change Payment Type"
            onClick={() => dispatch(clearPaymentType(null))}
            fill
          />

          {openTC && (
            <Layer
              onEsc={() => setOpenTC(false)}
              onClickOutside={() => setOpenTC(false)}
              margin="medium"
            >
              <TermsConditions
                closeLayer={() => setOpenTC(false)}
                acceptCheck={() => setAcceptTC(true)}
              />
            </Layer>
          )}
        </Box>
      )}
    </form>
  );
}
